import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { StyleCard } from "./StyleCard";
import { CustomReferenceCard } from "./CustomReferenceCard";
import { StylePreviewModal } from "./StylePreviewModal";
import { StyleTab, STYLE_TABS, type AdStyle } from "./types";
import { dictionary, Language } from "@/utils/dictionary";

export const MAX_SELECTION = 20;
export const MIN_SELECTION = 1;

interface StylesGalleryProps {
  templates: AdStyle[];
  isLoadingTemplates?: boolean;
  selectedStyles: string[];
  styleQuantities?: Record<string, number>;
  onToggleStyle: (id: string) => void;
  onAddStyle?: (id: string) => void;
  minSelection?: number;
  maxSelection?: number;
  customReferencePreview?: string;
  onCustomFileSelect: (file: File) => void;
  onCustomFileRemove: () => void;
  isCustomUploadLoading?: boolean;
  product?: { name: string; image: string; description?: string } | null;
  onTabChange?: (tab: StyleTab) => void;
  language: Language;
}

export function AdStudioGallery({
  templates,
  isLoadingTemplates = false,
  selectedStyles,
  onToggleStyle,
  minSelection = MIN_SELECTION,
  maxSelection = MAX_SELECTION,
  customReferencePreview,
  onCustomFileSelect,
  onCustomFileRemove,
  isCustomUploadLoading = false,
  product: _product,
  onTabChange,
  language,
}: StylesGalleryProps) {
  const [activeTab, setActiveTab] = useState<StyleTab>("all");
  const [previewStyle, setPreviewStyle] = useState<AdStyle | null>(null);
  const isMaxReached = maxSelection < 999 && selectedStyles.length >= maxSelection;

  const styles = useMemo(() => {
    if (activeTab === "all") return templates;
    if (activeTab === "custom") return templates.filter((s) => s.category === "custom" || s.isCustomUpload);
    return templates.filter((s) => s.category === activeTab);
  }, [templates, activeTab]);

  const browsableStyles = useMemo(
    () => templates.filter((s) => !!s.coverImage || s.isCustomUpload),
    [templates]
  );

  const getTabLabel = (tabId: StyleTab): string => {
    const key: Record<StyleTab, string> = {
      all: dictionary["all"]?.[language as Language] || "all",
      viral: dictionary["viral"]?.[language as Language] || "viral",
      offer: dictionary["offer"]?.[language as Language] || "offer",
      testimonials: dictionary["testimonials"]?.[language as Language] || "testimonials",
      custom: dictionary["custom"]?.[language as Language] || "custom",
    };
    return key[tabId];
  };

  const handleTabChange = (tab: StyleTab) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto p-5">
      {/* Selection counter banner — solo visible cuando hay seleccion */}
      {isMaxReached ? (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2.5">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex-shrink-0">
            !
          </div>
          <p className="text-sm text-amber-700 font-medium">
            {(dictionary["max-templates-reached"]?.[language as Language] || "Llegaste al máximo de {n} plantillas por generación").replace("{n}", String(maxSelection))}
          </p>
          <span className="ml-auto text-sm font-bold text-amber-700 tabular-nums">{selectedStyles.length}/{maxSelection}</span>
        </div>
      ) : selectedStyles.length > 0 ? (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#EEF0FF] px-4 py-2.5">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-fluxi text-white text-xs font-bold flex-shrink-0">
            {selectedStyles.length}
          </div>
          <p className="text-sm text-fluxi font-medium">
            {(dictionary["n-templates-selected"]?.[language as Language] || "{n} plantilla(s) seleccionada(s)").replace("{n}", String(selectedStyles.length))}
          </p>
          <span className="ml-auto text-sm font-bold text-fluxi tabular-nums">{selectedStyles.length}/{maxSelection}</span>
        </div>
      ) : null}

      {/* Tabs + counter */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide whitespace-nowrap pb-1">
          {STYLE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                "px-4 py-1.5 rounded-full text-sm font-medium transition-all",
                activeTab === tab.id
                  ? "bg-foreground text-background"
                  : "bg-white border border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              )}
            >
              {getTabLabel(tab.id)}
            </button>
          ))}
        </div>
      </div>

      {isLoadingTemplates ? (
        <div className="columns-3 gap-3 space-y-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <div
              key={i}
              className="w-full rounded-xl bg-muted animate-pulse"
              style={{ height: i % 3 === 0 ? "180px" : i % 3 === 1 ? "140px" : "160px" }}
            />
          ))}
        </div>
      ) : (
        <div className="columns-3 gap-3 space-y-3">
          {styles.map((style) => (
            <div key={style.id}>
              {style.isCustomUpload ? (
                <CustomReferenceCard
                  style={style}
                  isSelected={selectedStyles.includes(style.id)}
                  onToggle={onToggleStyle}
                  onExpand={(s) => setPreviewStyle(s)}
                  disabled={false}
                  previewUrl={customReferencePreview}
                  onFileSelect={onCustomFileSelect}
                  onRemoveFile={onCustomFileRemove}
                  isLoading={isCustomUploadLoading}
                  language={language}
                />
              ) : (
                <StyleCard
                  style={style}
                  isSelected={selectedStyles.includes(style.id)}
                  onToggle={onToggleStyle}
                  onExpand={(s) => setPreviewStyle(s)}
                  disabled={isMaxReached && !selectedStyles.includes(style.id)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {!isLoadingTemplates && styles.length === 0 && (
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          <p className="text-sm">{dictionary["no-styles-found"]?.[language as Language] || "No se encontraron estilos"}</p>
        </div>
      )}

      <StylePreviewModal
        allStyles={browsableStyles}
        initialStyle={previewStyle}
        open={!!previewStyle}
        onClose={() => setPreviewStyle(null)}
        selectedStyles={selectedStyles}
        styleQuantities={undefined}
        onToggle={onToggleStyle}
        onAdd={undefined}
        minSelection={minSelection}
        maxSelection={maxSelection ?? MAX_SELECTION}
        customReferencePreview={customReferencePreview}
        onCustomFileSelect={onCustomFileSelect}
        onCustomFileRemove={onCustomFileRemove}
        language={language}
      />
    </div>
  );
}
